import React from 'react';
import { HeartHandshakeIcon, UsersRoundIcon, SparklesIcon } from 'lucide-react';
import { Container, Section } from '../components/ui/Section';
import { PageHeader } from '../components/ui/PageHeader';
import { ButtonExternal, ButtonLink } from '../components/ui/Button';
import { DoodleUnderline } from '../components/brand/Doodles';
import { externalLinks } from '../data/site';

// TODO: Confirm the long-term giving processor and final destination with BCM
// leadership before production migration. This page intentionally links out to
// the existing verified giving page instead of rebuilding payment functionality.

const pillars = [{
  title: 'Bring In',
  copy: 'Welcome students and help them encounter the Gospel in a genuine Christian community.'
}, {
  title: 'Build Up',
  copy: 'Help students grow in faith, friendship, service, and leadership.'
}, {
  title: 'Send Out',
  copy: 'Equip students to live out their faith and serve on campus and beyond.'
}];
const otherWays = [{
  icon: UsersRoundIcon,
  title: 'Serve with us',
  copy: 'Students help lead worship, welcome new people, run outreach on campus, and serve at events throughout the year.'
}, {
  icon: SparklesIcon,
  title: 'Bring a friend',
  copy: 'One of the most valuable things anyone can do is invite a student who is looking for community to come on a Thursday.'
}, {
  icon: HeartHandshakeIcon,
  title: 'Partner with BCM',
  copy: 'Churches and individuals who want to talk about partnering with BCM can reach the team directly by email.'
}];
export function Give() {
  return <>
      <PageHeader eyebrow="Give" title="Support ministry to college students" intro="UCF BCM is supported by churches and individuals who invest in Christian community and ministry among college students." data-id="element-591" data-mp="36wujb6b6eiw1" />

      <Section className="bg-[#fffdf6] py-14 sm:py-18" labelledBy="giving-heading" data-id="element-592" data-mp="h5zbcucwh7ta">
        <Container className="grid items-start gap-8 lg:grid-cols-[1fr_0.85fr] lg:gap-12" data-id="element-593" data-mp="h8bqkzr9x42n">
          <div className="rounded-[24px] border-2 border-brand-navy bg-brand-blue p-7 sm:p-9" data-id="element-594" data-mp="3b01n1lkuoxab">
            <h2 id="giving-heading" className="font-serif text-2xl font-semibold leading-snug text-brand-navy sm:text-[1.9rem]" data-id="element-595" data-mp="3fz4vlg01hln1">
              Give to UCF BCM
            </h2>
            <DoodleUnderline className="mt-3 h-3 w-36" color="var(--bcm-dark-blue)" data-id="element-596" data-mp="2s7fwngsppyx4" />
            <p className="mt-5 font-sans text-[16px] leading-relaxed text-ink" data-id="element-597" data-mp="2s5cz8bp8d3id">
              Giving is handled through the existing UCF BCM giving page. The button below opens it in
              a new tab so your gift goes to the same verified destination it always has.
            </p>
            <ButtonExternal href={externalLinks.currentGivingPage} className="mt-7" data-id="element-598" data-mp="3605klqrsxdwv">
              Open Current Giving Page
            </ButtonExternal>
            <p className="mt-4 font-meta text-[12.5px] leading-relaxed text-ink" data-id="element-599" data-mp="2dhruyegmcy8v">
              Questions about giving? Email the BCM team and someone will help.
            </p>
            <div className="mt-4" data-id="element-600" data-mp="2107ay89zul0j">
              <ButtonExternal href={externalLinks.email} variant="secondary" data-id="element-601" data-mp="210z8vpr2cutz">
                Email About Giving
              </ButtonExternal>
            </div>
          </div>

          <div data-id="element-602" data-mp="1rzspn8x155x1">
            <h2 className="font-serif text-2xl font-semibold text-brand-navy" data-id="element-603" data-mp="1ry7txfyzbgxl">
              What your support makes possible
            </h2>
            <ul className="mt-6 space-y-5" data-id="element-604" data-mp="1n29b07wf6r4k">
              {pillars.map(pillar => <li key={pillar.title} className="border-l-4 border-brand-yellow pl-5" data-id="element-605" data-mp="1i8539mfbyf2f">
                  <h3 className="font-display text-xl font-bold text-brand-navy" data-id="element-606" data-mp="1dawg17fbrkub">{pillar.title}</h3>
                  <p className="mt-1.5 font-sans text-[15px] leading-relaxed text-ink" data-id="element-607" data-mp="18cv18yoxm7on">
                    {pillar.copy}
                  </p>
                </li>)}
            </ul>
          </div>
        </Container>
      </Section>

      <Section className="bcm-paper py-14 sm:py-18" labelledBy="other-ways-heading" data-id="element-608" data-mp="2zd3gbw43w093">
        <Container data-id="element-609" data-mp="33ef7i62motje">
          <h2 id="other-ways-heading" className="font-display text-3xl font-extrabold leading-tight tracking-tight text-brand-navy sm:text-4xl" data-id="element-610" data-mp="33eyewsunjzj6">
            Other ways to support BCM
          </h2>
          <ul className="mt-8 grid gap-5 sm:grid-cols-3" data-id="element-611" data-mp="2uktr6iatf47v">
            {otherWays.map(way => <li key={way.title} className="rounded-[22px] border-2 border-brand-navy bg-white p-6" data-id="element-612" data-mp="2fo2syccvnyju">
                <way.icon aria-hidden="true" className="h-6 w-6 text-brand-navy" data-id="element-613" data-mp="2kpicfzzepx04" />
                <h3 className="mt-3 font-display text-xl font-bold leading-snug text-brand-navy" data-id="element-614" data-mp="26p7jmreje8cb">
                  {way.title}
                </h3>
                <p className="mt-2 font-sans text-[15px] leading-relaxed text-ink" data-id="element-615" data-mp="2bp3oesv263sx">{way.copy}</p>
              </li>)}
          </ul>
          <div className="mt-10 flex flex-col gap-3 sm:flex-row sm:flex-wrap" data-id="element-616" data-mp="9w5okd2v6ayh">
            <ButtonExternal href={externalLinks.getConnectedForm} data-id="element-617" data-mp="jzb2lc6gkudt">Get Connected</ButtonExternal>
            <ButtonLink to="/about" variant="secondary" data-id="element-618" data-mp="f0zt52k2slw4">
              Learn More About BCM
            </ButtonLink>
          </div>
        </Container>
      </Section>
    </>;
}